import {
  CARD_DATA_ERROR_CODES,
  type CardDataKind,
  type CardDataOptions,
  isCardDataKind,
  isValidCardDataId,
} from "./cardDataKinds";

/** Host-only scope and operations. Resource IDs never come from iframe messages. */
export interface CardBridgeBinding {
  sessionVersion: number | null;
  execute(scope: "project" | "session", request: Record<string, unknown>): Promise<unknown>;
  /** Resolve one kind of host data on request (data bridge v2). Rejections carry a `code` from CARD_DATA_ERROR_CODES. */
  getData?(kind: CardDataKind, options: CardDataOptions): Promise<unknown>;
}
const MAX_MESSAGE_BYTES = 1024 * 1024 + 8192;
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function dataErrorCode(error: unknown): string {
  const code = isRecord(error) ? error.code : undefined;
  return typeof code === "string" && (CARD_DATA_ERROR_CODES as readonly string[]).includes(code)
    ? code
    : "DATA_UNAVAILABLE";
}
/** Bind a transferred port to exactly one host context. Returns synchronous teardown. */
export function bindCardPort(port: MessagePort, binding: CardBridgeBinding): () => void {
  let active = true;
  let pending = 0;
  let tokens = 20;
  let replenished = Date.now();
  const inFlight = new Set<string>();
  port.onmessage = async (event: MessageEvent<unknown>): Promise<void> => {
    if (!active || typeof event.data !== "string" || event.data.length > MAX_MESSAGE_BYTES) return;
    let request: unknown;
    try {
      request = JSON.parse(event.data);
    } catch {
      return;
    }
    if (!isRecord(request) || typeof request.requestId !== "string" || !/^[a-zA-Z0-9_-]{1,64}$/.test(request.requestId))
      return;
    const requestId = request.requestId;
    const reply = (result: unknown): void => {
      if (active) port.postMessage(JSON.stringify({ version: 1, requestId, ...(isRecord(result) ? result : {}) }));
    };
    const reject = (code: string): void => reply({ ok: false, error: { code } });
    const action = request.operation;
    const allowed =
      action === "context"
        ? ["version", "requestId", "operation"]
        : action === "data"
          ? ["version", "requestId", "operation", "kind", "id"]
          : action === "load"
            ? ["version", "requestId", "operation", "scope"]
            : action === "save"
              ? ["version", "requestId", "operation", "scope", "expectedRevision", "value"]
              : ["version", "requestId", "operation", "scope", "expectedRevision"];
    if (
      request.version !== 1 ||
      !["context", "data", "load", "save", "clear"].includes(String(action)) ||
      Object.keys(request).some((key) => !allowed.includes(key)) ||
      new TextEncoder().encode(event.data).length > MAX_MESSAGE_BYTES
    ) {
      reject("BAD_REQUEST");
      return;
    }
    if (inFlight.has(requestId)) return;
    const now = Date.now();
    tokens = Math.min(20, tokens + (now - replenished) / 100);
    replenished = now;
    if (tokens < 1 || pending >= 4) {
      reject("RATE_LIMITED");
      return;
    }
    tokens--;
    if (action === "context") {
      reply({ ok: true, result: { sessionVersion: binding.sessionVersion } });
      return;
    }
    if (action === "data") {
      const kind = request.kind;
      if (!isCardDataKind(kind) || !isValidCardDataId(kind, request.id)) {
        reject("BAD_REQUEST");
        return;
      }
      if (!binding.getData) {
        reject("NOT_AVAILABLE");
        return;
      }
      inFlight.add(requestId);
      pending++;
      try {
        const result = await binding.getData(kind, kind === "interaction" ? { id: request.id as string } : {});
        // Data replies are posted as objects (structured clone, no JSON string) so large values are not capped by
        // string limits. There is no size cap here: an oversized value fails in the browser, as the v1 push does.
        if (active) port.postMessage({ version: 1, requestId, ok: true, result });
      } catch (error) {
        reject(dataErrorCode(error));
      } finally {
        pending--;
        inFlight.delete(requestId);
      }
      return;
    }
    if (request.scope !== "project" && request.scope !== "session") {
      reject("BAD_REQUEST");
      return;
    }
    if (
      action !== "load" &&
      request.expectedRevision !== null &&
      (typeof request.expectedRevision !== "string" || request.expectedRevision.length > 256)
    ) {
      reject("BAD_REQUEST");
      return;
    }
    inFlight.add(requestId);
    pending++;
    try {
      const body: Record<string, unknown> = { operation: action };
      if (action !== "load") body.expectedRevision = request.expectedRevision;
      if (action === "save") body.value = request.value;
      const response = await binding.execute(request.scope, body);
      if (isRecord(response) && response.ok === true && isRecord(response.state))
        reply({ ok: true, result: response.state });
      else if (isRecord(response) && isRecord(response.error) && typeof response.error.code === "string")
        reject(response.error.code);
      else reject("STORAGE_UNAVAILABLE");
    } catch {
      reject("STORAGE_UNAVAILABLE");
    } finally {
      pending--;
      inFlight.delete(requestId);
    }
  };
  port.start();
  return () => {
    if (!active) return;
    port.postMessage(JSON.stringify({ type: "closed" }));
    active = false;
    port.onmessage = null;
    port.close();
  };
}
